import { ReactNode } from "react";

import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Stack } from "@mui/material";
import { QuestionType } from "@simplyk/common";

import { QuestionCard } from "./QuestionCard/QuestionCard";
import { CollapsibleQuestionSection } from "./QuestionSection/CollapsibleQuestionSection";
import { getQuestionTypeIcon, getQuestionTypeSubtitle } from "./helpers/questionTypeIcons";

import { useTranslate } from "@/common/hooks/useTranslate";
import { Location, Mail, Success, TextInput } from "@/icons/outlined";

interface BaseQuestion {
  localId: string;
  body: string;
  type: QuestionType;
  required: boolean;
  choices?: { body: string }[];
  scope: "buyer" | "participant";
}

interface MembershipLinkedQuestionsListV4Props<TQuestion extends BaseQuestion> {
  buyerQuestions: TQuestion[];
  memberQuestions: TQuestion[];
  onReorderBuyer: (event: DragEndEvent) => void;
  onReorderMember: (event: DragEndEvent) => void;
  onEdit: (question: TQuestion) => void;
  onDelete: (localId: string) => void;
  onAddBuyerQuestion: () => void;
  onAddMemberQuestion: () => void;
  askForAddress: boolean;
  onDeleteAddress: () => void;
  getQuestionIndex: (question: TQuestion) => number;
  getDeleteDisabledTooltip?: (question: TQuestion) => string | undefined;
  getLabelTooltip?: (question: TQuestion) => string | undefined;
  getQuestionLabel?: (question: TQuestion) => string | undefined;
  additionalBuyerOptions?: ReactNode;
}

export const MembershipLinkedQuestionsListV4 = <TQuestion extends BaseQuestion>({
  buyerQuestions,
  memberQuestions,
  onReorderBuyer,
  onReorderMember,
  onEdit,
  onDelete,
  onAddBuyerQuestion,
  onAddMemberQuestion,
  askForAddress,
  onDeleteAddress,
  getQuestionIndex,
  getDeleteDisabledTooltip,
  getLabelTooltip,
  getQuestionLabel,
  additionalBuyerOptions,
}: MembershipLinkedQuestionsListV4Props<TQuestion>) => {
  const { t } = useTranslate();

  const memberNameQuestion = memberQuestions.find((question) => question.type === QuestionType.Name);
  const sortableMemberQuestions = memberQuestions.filter((question) => question !== memberNameQuestion);

  const isBuyerSectionReorderable = buyerQuestions.length > 1;
  const isMemberSectionReorderable = sortableMemberQuestions.length > 1;

  const renderQuestionCards = (questions: TQuestion[], isSectionReorderable: boolean) =>
    questions.map((question) => (
      <QuestionCard
        key={question.localId}
        icon={getQuestionTypeIcon(question.type)}
        label={getQuestionLabel?.(question) ?? question.body}
        subtitle={getQuestionTypeSubtitle(t, question.type, question.choices)}
        required={question.required}
        sortableId={isSectionReorderable ? question.localId : undefined}
        onEdit={() => onEdit(question)}
        onDelete={() => onDelete(question.localId)}
        deleteDisabledTooltip={getDeleteDisabledTooltip?.(question)}
        labelTooltip={getLabelTooltip?.(question)}
        questionIndex={getQuestionIndex(question)}
        questionScope={question.scope}
      />
    ));

  return (
    <Stack spacing={3}>
      <CollapsibleQuestionSection
        title={t("dashboard", "stepQuestionsV4.section.member")}
        addButtonLabel={t("dashboard", "stepQuestionsV4.addMemberQuestion")}
        onAddQuestion={onAddMemberQuestion}
        addButtonTestId="v4-add-member-question-button"
        sectionTestId="v4-member-info-section"
      >
        {memberNameQuestion && (
          <QuestionCard
            icon={getQuestionTypeIcon(memberNameQuestion.type)}
            label={getQuestionLabel?.(memberNameQuestion) ?? memberNameQuestion.body}
            subtitle={t("dashboard", "stepQuestionsV4.typeShortText")}
            required={memberNameQuestion.required}
            deleteDisabledTooltip={t("dashboard", "stepQuestionsV4.memberNameDeleteTooltip")}
            questionIndex={getQuestionIndex(memberNameQuestion)}
            questionScope={memberNameQuestion.scope}
          />
        )}
        {sortableMemberQuestions.length > 0 && (
          <DndContext collisionDetection={closestCenter} onDragEnd={onReorderMember}>
            <SortableContext
              items={sortableMemberQuestions.map((question) => question.localId)}
              strategy={verticalListSortingStrategy}
            >
              {renderQuestionCards(sortableMemberQuestions, isMemberSectionReorderable)}
            </SortableContext>
          </DndContext>
        )}
      </CollapsibleQuestionSection>

      <CollapsibleQuestionSection
        title={t("dashboard", "stepQuestionsV4.section.buyer")}
        titleTooltip={t("dashboard", "stepQuestionsV4.section.buyer.tooltip")}
        addButtonLabel={t("dashboard", "stepQuestionsV4.addBuyerQuestion")}
        onAddQuestion={onAddBuyerQuestion}
        addButtonTestId="v4-add-buyer-question-button"
        sectionTestId="v4-buyer-info-section"
        defaultExpanded={false}
      >
        <QuestionCard
          icon={<TextInput />}
          label={t("dashboard", "stepQuestionsV4.buyerFullName")}
          subtitle={t("dashboard", "stepQuestionsV4.typeShortText")}
          required
          deleteDisabledTooltip={t("dashboard", "stepQuestionsV4.defaultQuestionTooltip")}
        />
        <QuestionCard
          icon={<Mail />}
          label={t("dashboard", "stepQuestionsV4.buyerEmail")}
          subtitle={t("dashboard", "donationForm.questionEmail")}
          required
          deleteDisabledTooltip={t("dashboard", "stepQuestionsV4.defaultQuestionTooltip")}
        />
        {askForAddress ? (
          <QuestionCard
            icon={<Location />}
            label={t("dashboard", "fullAddress")}
            subtitle={t("dashboard", "stepQuestionsV4.dialog.typeAddress")}
            required
            onDelete={onDeleteAddress}
          />
        ) : (
          <QuestionCard
            icon={<Success />}
            label={t("dashboard", "stateAndCountry")}
            subtitle={t("dashboard", "donationForm.uniqueQuestion")}
            required
            deleteDisabledTooltip={t("dashboard", "stepQuestionsV4.defaultQuestionTooltip")}
          />
        )}
        {buyerQuestions.length > 0 && (
          <DndContext collisionDetection={closestCenter} onDragEnd={onReorderBuyer}>
            <SortableContext
              items={buyerQuestions.map((question) => question.localId)}
              strategy={verticalListSortingStrategy}
            >
              {renderQuestionCards(buyerQuestions, isBuyerSectionReorderable)}
            </SortableContext>
          </DndContext>
        )}
        {additionalBuyerOptions}
      </CollapsibleQuestionSection>
    </Stack>
  );
};
